import { z } from "zod";

/**
 * Code-stable keys the application itself branches on. They are seeded, cannot
 * be deleted, and their `key` can never be edited — `contractor` in particular
 * gates the B2B billing-entity field on the person form, so a rename of the
 * admin-facing `name` must not disturb it.
 */
const PROTECTED_EMPLOYMENT_TYPE_KEYS = [
  "full_time",
  "part_time",
  "contractor",
  "variable",
] as const;

export function isProtectedEmploymentTypeKey(key: string): boolean {
  return (PROTECTED_EMPLOYMENT_TYPE_KEYS as readonly string[]).includes(key);
}

/**
 * Tokens are lowercase snake_case: they are stored on `person.employment_type`
 * and land verbatim in audit diffs, so they need to stay greppable and stable.
 */
const employmentTypeKey = z
  .string()
  .trim()
  .min(1, "Key is required")
  .max(60, "Key is too long")
  .regex(/^[a-z][a-z0-9_]*$/, "Use lowercase letters, numbers and underscores, e.g. seasonal");

/**
 * NULL means this type carries no weekly expectation at all — the Time Cards
 * comparison is suppressed rather than reported as a full shortfall. That is
 * the right answer for Variable, and the starting point for Part time and
 * Contractor until someone fills them in.
 */
const expectedWeeklyHours = z
  .number()
  .min(0, "Hours can't be negative")
  .max(168, "There are only 168 hours in a week")
  .nullable()
  .optional();

// Default-free base. Per the zod-v4 trap this project has hit before (WI-061),
// `.partial()` still fires `.default()`s, so no field here may carry one.
const employmentTypeFieldsSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120, "Name is too long"),
  expectedWeeklyHours,
  isActive: z.boolean().optional(),
});

export const employmentTypeCreateSchema = employmentTypeFieldsSchema.extend({
  key: employmentTypeKey,
});

/**
 * `key` is deliberately absent: it is immutable once created, because code
 * branches on it and every historical audit diff already stores it.
 */
export const employmentTypeUpdateSchema = employmentTypeFieldsSchema.partial();

export const employmentTypeReorderSchema = z.object({
  direction: z.enum(["up", "down"]),
});

export type EmploymentTypeCreateInput = z.infer<typeof employmentTypeCreateSchema>;
export type EmploymentTypeUpdateInput = z.infer<typeof employmentTypeUpdateSchema>;
